import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import UploadButton from "@/components/UploadButton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, LifeBuoy, ChevronDown, ChevronUp, CheckCircle2, Paperclip, X } from "lucide-react";

const APP_VERSION = "2026.09-preview";

const FEEDBACK_TYPES = [
  { value: "bug", label: "Something is not working" },
  { value: "feature_request", label: "Suggest an improvement" },
  { value: "usability", label: "Something is confusing or difficult" },
  { value: "compliment", label: "Something worked especially well" },
  { value: "content_or_category", label: "Content or category feedback" },
  { value: "safety_or_trust", label: "Safety or trust concern" },
  { value: "other", label: "Other feedback" },
];

const FEATURE_AREAS = [
  { value: "signup_login", label: "Sign in / create account" },
  { value: "profile", label: "Profile" },
  { value: "search_discovery", label: "Search & discovery" },
  { value: "my_board", label: "My Board" },
  { value: "feed", label: "Feed" },
  { value: "wall", label: "Wall" },
  { value: "messaging", label: "Messaging" },
  { value: "hire_requests", label: "Hire requests" },
  { value: "reviews", label: "Reviews" },
  { value: "verification", label: "Verification" },
  { value: "billing_subscription", label: "Billing & subscription" },
  { value: "feedback", label: "Feedback" },
  { value: "accessibility", label: "Accessibility" },
  { value: "other", label: "Other" },
];

const IMPACT_LEVELS = [
  { value: "blocked", label: "Blocked me completely" },
  { value: "major_difficulty", label: "Major difficulty" },
  { value: "minor_difficulty", label: "Minor difficulty" },
  { value: "suggestion_only", label: "Suggestion only" },
  { value: "positive", label: "Positive feedback" },
];

const ACCESS_HELP_ISSUES = [
  { value: "login", label: "Signing in" },
  { value: "registration", label: "Creating an account" },
  { value: "email_verification", label: "Verifying my email" },
  { value: "password_reset", label: "Resetting my password" },
];

export default function Feedback() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#F2F4F2]">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-14" data-testid="feedback-page">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Feedback</h1>
        <p className="text-sm text-[#222]/60 mb-3">Tell us what's working, what's not, or what you'd love to see next on LIMYA.</p>
        <p className="text-sm text-[#222321] bg-[#FDF4E3] border border-[#E0A83E]/40 rounded-xl px-4 py-3 mb-8 flex items-start gap-2" data-testid="feedback-beta-note">
          <MessageCircle size={15} className="text-[#E0A83E] shrink-0 mt-0.5"/>
          We're building LIMYA with you. Tell us what's working, what isn't, or what you'd love to see.
        </p>
        {user ? <AuthenticatedFeedbackForm /> : <LoggedOutGateway />}
      </div>
      <Footer />
    </div>
  );
}

const EMPTY_FORM = {
  feedback_type: "usability", feature_area: "other", subject: "", user_goal: "",
  message: "", impact: "minor_difficulty",
  bug_what_happened: "", bug_expected: "", bug_steps: "", bug_always_happens: "",
  attachment_url: "", follow_up_ok: null,
};

function AuthenticatedFeedbackForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isBug = form.feedback_type === "bug";

  const submit = async (e) => {
    e.preventDefault();
    if (busy || confirmation) return; // guards against accidental duplicate submits
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Please fill in a subject and feedback details."); return; }
    if (isBug && !form.bug_what_happened.trim()) { toast.error("Please describe what happened."); return; }
    if (form.follow_up_ok === null) { toast.error("Please let us know if we may follow up with you."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", {
        ...form,
        subject: form.subject.trim(), message: form.message.trim(), user_goal: form.user_goal.trim(),
        page_route: document.referrer || window.location.pathname, app_version: APP_VERSION,
      });
      setConfirmation({ ref_id: data.ref_id, status: data.status });
      // Form is only cleared once the backend confirms the record was saved.
      setForm(EMPTY_FORM);
      toast.success("Thanks — your feedback was sent to the LIMYA team.");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not save your feedback — please try again. Your entry has not been cleared.");
    } finally {
      setBusy(false);
    }
  };

  if (confirmation) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-black/5" data-testid="feedback-confirmation">
        <CheckCircle2 size={32} className="mx-auto mb-3 text-[#245544]" />
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: "Outfit" }}>Thanks — your feedback was sent to the LIMYA team.</h2>
        <p className="text-sm text-[#222]/60 mb-3">We read every note that comes in during the public beta.</p>
        <p className="inline-block px-4 py-2 rounded-full bg-black/[0.04] text-sm font-mono font-semibold" data-testid="feedback-reference-number">
          Reference: {confirmation.ref_id}
        </p>
        <p className="text-xs text-[#222]/45 mt-3">Keep this reference number if you'd like to follow up on this feedback.</p>
        <button onClick={() => setConfirmation(null)} className="mt-6 text-sm underline text-[#245544]" data-testid="feedback-submit-another">
          Submit another feedback
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 space-y-4" data-testid="feedback-form">
      <div className="flex items-start gap-2 text-xs text-[#222]/55 bg-black/[0.03] rounded-xl px-3 py-2.5">
        <MessageCircle size={14} className="shrink-0 mt-0.5" />
        Technical context (account type, page, browser) is attached automatically to help LIMYA investigate — you never need to describe your plan or role.
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-semibold block mb-1.5">Feedback type</label>
          <select value={form.feedback_type} onChange={e => set("feedback_type", e.target.value)} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="feedback-type-select">
            {FEEDBACK_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold block mb-1.5">Feature area</label>
          <select value={form.feature_area} onChange={e => set("feature_area", e.target.value)} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="feedback-area-select">
            {FEATURE_AREAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold block mb-1.5">Subject</label>
        <input value={form.subject} onChange={e => set("subject", e.target.value)} maxLength={150} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" placeholder="Short summary" data-testid="feedback-subject-input" />
      </div>

      <div>
        <label className="text-sm font-semibold block mb-1.5">What were you trying to accomplish?</label>
        <input value={form.user_goal} onChange={e => set("user_goal", e.target.value)} maxLength={500} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" placeholder="Your goal" data-testid="feedback-goal-input" />
      </div>

      <div>
        <label className="text-sm font-semibold block mb-1.5">Feedback details</label>
        <textarea value={form.message} onChange={e => set("message", e.target.value)} maxLength={3000} rows={5} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" placeholder="The more detail, the better." data-testid="feedback-message-input" />
      </div>

      <div>
        <label className="text-sm font-semibold block mb-1.5">Impact</label>
        <select value={form.impact} onChange={e => set("impact", e.target.value)} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="feedback-impact-select">
          {IMPACT_LEVELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {isBug && (
        <div className="space-y-3 border border-amber-200 bg-amber-50 rounded-xl p-4" data-testid="feedback-bug-fields">
          <p className="text-xs font-semibold text-amber-800">Since this is a bug report, a few more details help us fix it faster:</p>
          <div>
            <label className="text-sm font-semibold block mb-1.5">What happened?</label>
            <textarea value={form.bug_what_happened} onChange={e => set("bug_what_happened", e.target.value)} rows={3} maxLength={1500} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="feedback-bug-what-happened" />
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5">What did you expect to happen?</label>
            <textarea value={form.bug_expected} onChange={e => set("bug_expected", e.target.value)} rows={2} maxLength={1500} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="feedback-bug-expected" />
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5">Steps to reproduce</label>
            <textarea value={form.bug_steps} onChange={e => set("bug_steps", e.target.value)} rows={2} maxLength={1500} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" placeholder="1. … 2. … 3. …" data-testid="feedback-bug-steps" />
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5">Does it happen every time?</label>
            <select value={form.bug_always_happens} onChange={e => set("bug_always_happens", e.target.value)} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="feedback-bug-always">
              <option value="">Select…</option>
              <option value="yes">Yes, every time</option>
              <option value="sometimes">Sometimes</option>
              <option value="no">No, it happened once</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5">Attachment or screenshot (optional)</label>
            {form.attachment_url ? (
              <div className="flex items-center gap-2 text-xs text-[#222]/60">
                <Paperclip size={13} /> Attached
                <button type="button" onClick={() => set("attachment_url", "")} data-testid="feedback-attachment-remove"><X size={13} /></button>
              </div>
            ) : (
              <UploadButton onUploaded={(url) => set("attachment_url", url)} label="Attach screenshot" accept="image/*" />
            )}
          </div>
        </div>
      )}

      <div>
        <label className="text-sm font-semibold block mb-1.5">May LIMYA contact you about this feedback?</label>
        <div className="flex gap-2">
          <button type="button" onClick={() => set("follow_up_ok", true)} className={`px-4 py-2 rounded-full text-sm font-semibold border ${form.follow_up_ok === true ? "bg-[#222] text-white border-[#222]" : "border-black/10"}`} data-testid="feedback-followup-yes">Yes</button>
          <button type="button" onClick={() => set("follow_up_ok", false)} className={`px-4 py-2 rounded-full text-sm font-semibold border ${form.follow_up_ok === false ? "bg-[#222] text-white border-[#222]" : "border-black/10"}`} data-testid="feedback-followup-no">No</button>
        </div>
      </div>

      <button type="submit" disabled={busy} className="btn-lime px-6 py-2.5 text-sm disabled:opacity-50" data-testid="feedback-submit-button">
        {busy ? "Sending…" : "Send feedback"}
      </button>
    </form>
  );
}

function LoggedOutGateway() {
  const [showAccessHelp, setShowAccessHelp] = useState(false);
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 text-center" data-testid="feedback-signin-gateway">
        <MessageCircle size={28} className="mx-auto mb-3 text-[#2E6B57]" />
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: "Outfit" }}>Sign in to send feedback</h2>
        <p className="text-sm text-[#222]/60 mb-5">We ask you to sign in so we can look into your feedback with the right account context — no forms to fill in about your account type.</p>
        <div className="flex items-center justify-center gap-3">
          <Link to="/login" className="btn-lime px-5 py-2.5 text-sm" data-testid="feedback-signin-link">Sign in</Link>
          <Link to="/register" className="px-5 py-2.5 text-sm rounded-full border border-black/10 hover:bg-black/5 transition" data-testid="feedback-register-link">Create an account</Link>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid="feedback-access-help-section">
        <button onClick={() => setShowAccessHelp(!showAccessHelp)} className="w-full flex items-center justify-between px-6 py-4 text-left" data-testid="feedback-access-help-toggle">
          <span className="flex items-center gap-2 text-sm font-semibold"><LifeBuoy size={16} className="text-[#222]/50" /> Trouble signing in or creating an account?</span>
          {showAccessHelp ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showAccessHelp && <AccessHelpForm />}
      </div>
    </div>
  );
}

function AccessHelpForm() {
  const [issueType, setIssueType] = useState("login");
  const [contactEmail, setContactEmail] = useState("");
  const [message, setMessage] = useState("");
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || confirmation) return;
    if (!contactEmail.trim() || !message.trim()) { toast.error("Please provide a contact email and a message."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/feedback/access-help", { contact_email: contactEmail.trim(), issue_type: issueType, message: message.trim(), hp });
      setConfirmation({ ref_id: data.ref_id });
      toast.success("Thanks — we'll follow up at the email you provided.");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not send your message right now. Your entry has not been cleared.");
    } finally {
      setBusy(false);
    }
  };

  if (confirmation) {
    return (
      <div className="px-6 pb-6 border-t border-black/5 pt-4" data-testid="feedback-access-help-sent">
        <p className="text-sm text-[#222]/70 flex items-center gap-2"><CheckCircle2 size={15} className="text-[#245544]" /> Thanks — we'll follow up by email as soon as we can.</p>
        <p className="text-xs text-[#222]/45 mt-2 font-mono">Reference: {confirmation.ref_id}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="px-6 pb-6 space-y-3 border-t border-black/5 pt-4 relative" data-testid="access-help-form">
      <p className="text-xs text-[#222]/50">This form is only for account access issues — registration, sign-in, email verification, or password reset. For anything else, please sign in first.</p>
      <div>
        <label className="text-sm font-semibold block mb-1.5">What's going wrong?</label>
        <select value={issueType} onChange={e => setIssueType(e.target.value)} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="access-help-issue-select">
          {ACCESS_HELP_ISSUES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-sm font-semibold block mb-1.5">Contact email</label>
        <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" placeholder="you@example.com" data-testid="access-help-email-input" />
      </div>
      <div>
        <label className="text-sm font-semibold block mb-1.5">What happened?</label>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} maxLength={2000} className="w-full px-3 py-2.5 border border-black/10 rounded-xl text-sm" data-testid="access-help-message-input" />
      </div>
      {/* Honeypot — invisible to real users; bots often fill every field */}
      <input type="text" value={hp} onChange={e => setHp(e.target.value)} className="absolute -left-[9999px]" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="access-help-honeypot" />
      <button type="submit" disabled={busy} className="px-5 py-2.5 text-sm rounded-full bg-[#222] text-white disabled:opacity-50" data-testid="access-help-submit-button">
        {busy ? "Sending…" : "Send"}
      </button>
    </form>
  );
}
